import fileUploadLogo from "../../assets/img/UploadCertificate.svg";
import { ApiCompany } from "../../core/apis/company";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { Button } from "../../components/button.component";
import { Form, Toggle } from "rsuite";
import { useHistory } from "react-router";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  TextInput,
  FileInput,
  SelectPickerInput,
  NumberInput,
  TagInputWithIcon,
} from "../../components/textInput.component";
import { CompanyFormSchema } from "../../models/forms/companyForm";
import imageUploadLogo from "../../assets/img/picture-upload.svg";
import { UploadedFileType } from "../../models/forms/uploadFileType";
import {
  DoesObjectHasAnyInvalidValue,
  generateMultipartFormData,
} from "../../utils/formUtils";
import { ActionsCompanyTable } from "../../core/redux/slices/sliceCompanyTable";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { SubscriptionTypeRecords } from "../../config/subscriptionType";
import { SpinLoader } from "../../components/spinLoader.component";
import { Utility, timeZoneRecords } from "../../config";
import { isBefore } from "date-fns";
import { ApiInsuranceTypes } from "../../core/apis/insuranceTypes";

type CompanyFormData = {
  Name: string;
  DisplayName: string;
  Email: string;
  Phone: string;
  TaxId: string;
  Address: string;
  RepresentativeName: string;
  RepresentativePhone: string;
  Rate?: string;
  Documents: UploadedFileType[];
  CompanyLogo: UploadedFileType[];
  SubscriptionType: string;
  TimeZone: string;
  SuccessUrl: string;
  CancelUrl: string;
  ProviderNumber: string;
  insuranceTypeIds: string[];
};

export const UpdateCompany = () => {
  const routeHistory = useHistory();
  const { companyId } = useParams<{ companyId: string }>();
  const { data, isValidating } = ApiCompany.ById.Hook(companyId);
  const { data: insuranceTypes } = ApiInsuranceTypes.GetInsuranceTypes.Hook();
  const previousSubscriptionType = useMemo(
    () =>
      isBefore(new Date(data?.subscription?.endDate || ""), new Date())
        ? "Expired"
        : data?.subscription?.subscriptionType,
    [data?.subscription?.endDate, data?.subscription?.subscriptionType]
  );
  const [isAutoClockInEnable, setIsAutoClockInEnable] = useState(false);
  const [isGpsCheckInEnable, setIsGpsCheckInEnable] = useState(false);
  const [formError, setFormError] = useState({});
  const [formData, setFormData] = useState<Partial<CompanyFormData>>({
    Name: "",
    DisplayName: "",
    Email: "",
    Phone: "",
    TaxId: "",
    Address: "",
    RepresentativeName: "",
    RepresentativePhone: "",
    Rate: "0",
    Documents: [],
    CompanyLogo: [],
    SubscriptionType: "",
    SuccessUrl: window.location.origin + AppRouteUi.Company.List(),
    CancelUrl: window.location.origin + AppRouteUi.Company.List(),
    ProviderNumber: "",
    insuranceTypeIds: [],
    TimeZone: "",
  });

  const [removeDocuments, setRemoveDocuments] = useState<string[]>([]);

  const handleDocumentClick = (doc: string) => {
    removeDocuments.includes(doc)
      ? setRemoveDocuments(
          removeDocuments.filter((existing) => existing !== doc)
        )
      : setRemoveDocuments([...removeDocuments, doc]);
  };

  const insuranceTypeMap =
    insuranceTypes?.map((it) => ({
      label: it.displayName ?? it.name,
      value: it.sKey,
    })) ?? [];

  useEffect(() => {
    setFormData({
      Name: data?.name,
      DisplayName: data?.displayName,
      Email: data?.email,
      Phone: data?.phone,
      TaxId: data?.taxId,
      Address: data?.address,
      RepresentativeName: data?.representativeName,
      RepresentativePhone: data?.representativePhone,
      Rate: `${data?.rate}`,
      SubscriptionType: isBefore(
        new Date(data?.subscription?.endDate || ""),
        new Date()
      )
        ? ""
        : data?.subscription?.subscriptionType,
      SuccessUrl: window.location.origin + AppRouteUi.Company.List(),
      CancelUrl: window.location.origin + AppRouteUi.Company.List(),
      ProviderNumber: data?.providerNumber,
      insuranceTypeIds: data?.insuranceTypes?.length
        ? data?.insuranceTypes.map((x) => x.sKey)
        : [],
      TimeZone: data?.timeZone || timeZoneRecords[12].value,
    });
    setIsAutoClockInEnable(!!data?.isAutoClockInEnable);
    setIsGpsCheckInEnable(!!data?.isGpsCheckInEnable);
    convertToPhoneNumberFormat(
      data?.representativePhone ? data?.representativePhone : "",
      "RepresentativePhone"
    );
  }, [data]);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const {
      Phone,
      Address,
      RepresentativeName,
      RepresentativePhone,
      TimeZone,
      ...rest
    } = formData;
    const isEmpty = Object.values({ ...rest }).some(
      DoesObjectHasAnyInvalidValue
    );
    const errorCount = Object.keys(formError).length;
    var isDisabled = isEmpty || !!errorCount;
    setIsDisabled(isDisabled);
  }, [formData, formError]);

  const handleChange = useCallback(
    (data: any, _: any) =>
      setFormData((prevFormData) => ({ ...prevFormData, ...data })),
    []
  );

  const convertToPhoneNumberFormat = (
    phoneNumber: string,
    propertyName: string
  ) => {
    phoneNumber = phoneNumber.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength > 3 && phoneNumberLength < 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumberLength >= 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6, 10)}`;
    }

    setFormData((prev) => ({ ...prev, [propertyName]: phoneNumber }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const multipartFormData = generateMultipartFormData(formData);

    multipartFormData.append(
      "IsAutoClockInEnable",
      isAutoClockInEnable ? "true" : "false"
    );

    multipartFormData.append(
      "IsGpsCheckInEnable",
      isGpsCheckInEnable ? "true" : "false"
    );

    removeDocuments.forEach((doc) =>
      multipartFormData.append("RemoveDocuments", doc)
    );

    multipartFormData.append("SKey", companyId);

    const result = await ApiCompany.Update(multipartFormData);

    if (result.data?.sessionId) {
      Utility.getStripe()?.redirectToCheckout({
        sessionId: result.data.sessionId,
      });
    } else if (result.isSuccess) {
      dispatch(ActionsCompanyTable.goToPage(1));
      routeHistory.length > 1
        ? routeHistory.goBack()
        : routeHistory.push(AppRouteUi.Root);
    }

    setIsLoading(false);
  };

  return (
    <>
      {isValidating ? (
        <SpinLoader />
      ) : (
        <Form
          className="row"
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCheck={setFormError}
          model={CompanyFormSchema}
          formValue={formData}
          fluid
        >
          <div className="col-md-10 col-12 border-end px-4 add-page-ctl">
            <div className="row">
              <TextInput
                name="Name"
                required
                onChange={(val) =>
                  setFormData({ ...formData, Name: val, DisplayName: val })
                }
              >
                Name
              </TextInput>
              <TextInput name="DisplayName" required>
                Display Name
              </TextInput>

              <TextInput name="Email" required>
                Email
              </TextInput>

              <NumberInput name="Rate" min={0}>
                Company's rate
              </NumberInput>

              <TextInput name="TaxId" required>
                Provider Tax ID
              </TextInput>

              <TextInput name="ProviderNumber" required>
                NPI/Provider Number
              </TextInput>

              <TagInputWithIcon
                name="insuranceTypeIds"
                data={insuranceTypeMap}
                required
                value={formData.insuranceTypeIds}
                placeholder="Select Insurance Types"
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Insurance type
              </TagInputWithIcon>

              <TextInput name="RepresentativeName">
                Representative Name
              </TextInput>
              <TextInput
                name="RepresentativePhone"
                value={formData.RepresentativePhone}
                onChange={(val) =>
                  convertToPhoneNumberFormat(val, "RepresentativePhone")
                }
              >
                Representative Phone
              </TextInput>

              <TextInput name="Address">Address</TextInput>
              <SelectPickerInput
                name="SubscriptionType"
                required
                data={SubscriptionTypeRecords}
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Subscription Type
              </SelectPickerInput>
              <SelectPickerInput
                name="TimeZone"
                data={timeZoneRecords}
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Time Zone
              </SelectPickerInput>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                Auto clock check in
                <Toggle
                  onChange={() => setIsAutoClockInEnable(!isAutoClockInEnable)}
                  checked={isAutoClockInEnable}
                  size="xs"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                GPS check in
                <Toggle
                  onChange={() => setIsGpsCheckInEnable(!isGpsCheckInEnable)}
                  checked={isGpsCheckInEnable}
                  size="xs"
                  className="ms-2"
                />
              </div>
            </div>
            <div className="row">
              <FileInput
                listType="picture"
                imageSrc={imageUploadLogo}
                name="CompanyLogo"
                accept="image/*"
                imageSrcApi={
                  data?.companyLogo
                    ? AppRouteApi.Media.ByPath(data?.companyLogo || "")
                    : imageUploadLogo
                }
              >
                Upload Company Logo
              </FileInput>
            </div>

            <div className="row">
              <FileInput
                listType="text"
                imageSrc={fileUploadLogo}
                name="Documents"
                multiple
              >
                Upload Documents
              </FileInput>
              {data?.documents.length ? (
                <div>
                  <p>Uploaded Documents: </p>
                  <div className="d-flex flex-column">
                    {data?.documents.map((doc) => (
                      <div
                        key={doc}
                        className="py-1 d-flex uploadedItem align-items-center"
                        style={{
                          color: removeDocuments.includes(doc)
                            ? "red"
                            : "black",
                        }}
                        onClick={() => {
                          handleDocumentClick(doc);
                        }}
                      >
                        <img
                          src={imageUploadLogo}
                          alt="Uploaded Item"
                          className="me-2"
                          height={35}
                        />
                        <p className="text-break w-100">
                          {doc.split("_").slice(4).join("_")}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                "No uploaded documents"
              )}
            </div>
            {previousSubscriptionType !== "Premium" &&
              formData.SubscriptionType === "Premium" && (
                <div className="alert alert-success mt-1" role="alert">
                  You will be redirected to payment page after you submit
                </div>
              )}
          </div>

          <div className="col-md-2 col-12">
            <Button
              className="m-1 w-100 btn-green"
              type="submit"
              name="Save"
              loading={isLoading}
              disabled={isDisabled}
            />
            <Button
              onClick={() => {
                routeHistory.length > 1
                  ? routeHistory.goBack()
                  : routeHistory.push(AppRouteUi.Root);
              }}
              type="button"
              className="m-1 w-100 btn-green-subtle"
              disabled={isLoading}
              name="Return without saving"
            />
          </div>
        </Form>
      )}
    </>
  );
};
